<script lang="ts">
  import { onMount, type ComponentType, onDestroy } from "svelte";
  import { cubicInOut } from "svelte/easing";
  import { fade, fly } from "svelte/transition";
  import { createMutation } from "@tanstack/svelte-query";
  import {
    unlinkTransactions,
    type TransactionType,
    type GroupedTransaction,
    resolveTransaction,
  } from "../../api/transactions";
  import { linkTransactionsModal } from "../../stores/link-transactions-modal-store";
  import OrderTransactionPanel from "../invoices/transactions/OrderTransactionPanel.svelte";
  import DocumentTransactionPanel from "../invoices/transactions/DocumentTransactionPanel.svelte";
  import LinkTransactionPanel from "../invoices/transactions/LinkTransactionPanel.svelte";
  import { canUnlinkTransaction, getEntityId } from "../invoices/transactions/data";
  import { transactionKeys } from "../../queries/transactions";
  import { queryClient } from "../../helpers/query-client";
  import IssuesTransactionPanel from "../invoices/transactions/IssuesTransactionPanel.svelte";
  import { DEFAULT_FADE_TRANSITION } from "./transitions";
  import { track } from "../../helpers/mixpanel";

  const EXIT_DURATION = 200;
  const DEFAULT_FLY_TRANSITION = { x: "40px", duration: 300, easing: cubicInOut };
  const EXIT_FLY_TRANSITION = { ...DEFAULT_FLY_TRANSITION, duration: EXIT_DURATION };

  const PANEL_TYPE_MAP: Record<TransactionType, ComponentType> = {
    invoice_arrived: DocumentTransactionPanel,
    manual_invoice: DocumentTransactionPanel,
    pro_invoice_arrived: DocumentTransactionPanel,
    payment: DocumentTransactionPanel,
    order: OrderTransactionPanel,
    refund: DocumentTransactionPanel,
    creditnote: DocumentTransactionPanel,
    checkin: IssuesTransactionPanel,
    credit_request: IssuesTransactionPanel,
  };

  function onClose() {
    let delay = showLinkingModule || secondaryTransaction ? EXIT_DURATION : 0;
    if (showLinkingModule) {
      linkTransactionsModal.hideLinkingModule();
    }

    if (secondaryTransaction) {
      linkTransactionsModal.setSecondaryTransaction(null);
    }

    setTimeout(() => {
      linkTransactionsModal.close();
    }, delay);
  }

  function onOpenTransaction(transaction: GroupedTransaction) {
    let delay = showLinkingModule ? EXIT_DURATION : 0;

    if (showLinkingModule) {
      linkTransactionsModal.hideLinkingModule();
    }

    setTimeout(() => {
      linkTransactionsModal.setSecondaryTransaction(transaction);
    }, delay);
  }

  function onCloseTransaction() {
    linkTransactionsModal.setSecondaryTransaction(null);
  }

  function onAddConnection() {
    let delay = secondaryTransaction ? EXIT_DURATION : 0;

    if (secondaryTransaction) {
      linkTransactionsModal.setSecondaryTransaction(null);
    }

    setTimeout(() => {
      linkTransactionsModal.showLinkingModule();
    }, delay);
  }

  function onCloseConnections() {
    linkTransactionsModal.hideLinkingModule();
    linkTransactionsModal.setSecondaryTransaction(null);
  }

  const unlinkTransactionMutation = createMutation({
    mutationFn: unlinkTransaction,
    onSuccess: () => {
      if (!primaryTransaction) return;
      queryClient.invalidateQueries({
        queryKey: transactionKeys.relatedTransactions(
          primaryTransaction.type,
          getEntityId(primaryTransaction)
        ),
      });
      queryClient.invalidateQueries({
        queryKey: transactionKeys.groupedByBuyerId(primaryTransaction.buyer_id),
      });
      track("unlink_transaction", {
        primary_transaction_id: getEntityId(primaryTransaction),
        primaryTransactionType: primaryTransaction.type,
        secondary_transaction_id: getEntityId(secondaryTransaction),
        secondaryTransactionType: secondaryTransaction?.type,
      });
      linkTransactionsModal.setSecondaryTransaction(null);
    },
  });

  async function unlinkTransaction() {
    if (primaryTransaction && secondaryTransaction) {
      await unlinkTransactions(
        primaryTransaction.buyer_id,
        primaryTransaction?.type,
        getEntityId(primaryTransaction),
        secondaryTransaction?.type,
        getEntityId(secondaryTransaction)
      );
    }
  }

  const resolveTransactionMutation = createMutation({
    mutationFn: async (transaction: GroupedTransaction | null) => {
      if (transaction && !transaction.is_resolved) {
        await resolveTransaction(transaction.buyer_id, transaction.type, getEntityId(transaction));
      }
    },
    onSuccess: () => {
      if (primaryTransaction) {
        queryClient.invalidateQueries({
          queryKey: transactionKeys.relatedTransactions(
            primaryTransaction.type,
            getEntityId(primaryTransaction)
          ),
        });
        queryClient.invalidateQueries({
          queryKey: transactionKeys.issueDetails(
            primaryTransaction.type,
            getEntityId(primaryTransaction)
          ),
        });
        queryClient.invalidateQueries({
          queryKey: transactionKeys.groupedByBuyerId(primaryTransaction.buyer_id),
        });
        onClose();
        track("resolve_transaction", {
          primary_transaction_id: getEntityId(primaryTransaction),
          primaryTransactionType: primaryTransaction.type,
        });
      }
    },
  });

  $: primaryTransaction = $linkTransactionsModal.primaryTransaction;
  $: PrimaryTransactionPanel = primaryTransaction?.type
    ? PANEL_TYPE_MAP[primaryTransaction.type]
    : null;

  $: secondaryTransaction = $linkTransactionsModal.secondaryTransaction;
  $: SecondaryTransactionPanel = secondaryTransaction
    ? PANEL_TYPE_MAP[secondaryTransaction?.type]
    : null;

  $: unlinkAction = canUnlinkTransaction(primaryTransaction, secondaryTransaction)
    ? () => $unlinkTransactionMutation.mutate()
    : null;
  $: showLinkingModule = $linkTransactionsModal.showLinkingModule;

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape" && onClose) {
      if (showLinkingModule) {
        linkTransactionsModal.hideLinkingModule();
      } else if (secondaryTransaction) {
        linkTransactionsModal.setSecondaryTransaction(null);
      } else {
        onClose();
      }
    }
  }

  onMount(() => {
    document.addEventListener("keydown", handleKeyDown);
  });

  // Remove event listener when component unmounts
  onDestroy(() => {
    document.removeEventListener("keydown", handleKeyDown);
  });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
{#if $linkTransactionsModal.isVisible && $linkTransactionsModal.primaryTransaction}
  <div class="modal-container" on:click={onClose}>
    <div class="underlay" transition:fade|global={DEFAULT_FADE_TRANSITION} />
    {#if primaryTransaction}
      <div
        class="primary-transaction"
        on:click|stopPropagation
        transition:fly|global={DEFAULT_FLY_TRANSITION}
      >
        <svelte:component
          this={PrimaryTransactionPanel}
          props={{
            transaction: primaryTransaction,
            onOpenTransaction,
            onCloseTransaction,
            onAddConnection,
            onCloseConnections,
            onClose,
            onResolveTransaction: () => $resolveTransactionMutation.mutate(primaryTransaction),
          }}
        />
      </div>
    {/if}

    {#if secondaryTransaction}
      <div
        class="secondary-transaction"
        on:click|stopPropagation
        in:fly|global={DEFAULT_FLY_TRANSITION}
        out:fly|global={EXIT_FLY_TRANSITION}
      >
        <svelte:component
          this={SecondaryTransactionPanel}
          props={{
            transaction: secondaryTransaction,
            onUnlinkTransaction: unlinkAction,
            onResolveTransaction: () => $resolveTransactionMutation.mutate(secondaryTransaction),
            showConnections: false,
          }}
        />
      </div>
    {/if}

    {#if showLinkingModule}
      <div
        class="secondary-transaction"
        on:click|stopPropagation
        in:fly|global={DEFAULT_FLY_TRANSITION}
        out:fly|global={EXIT_FLY_TRANSITION}
      >
        <LinkTransactionPanel />
      </div>
    {/if}
  </div>
{/if}

<style>
  .modal-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 12px;
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-start;
    align-items: stretch;
    overflow: hidden;
    z-index: 100;
  }

  .primary-transaction,
  .secondary-transaction {
    display: flex;
    justify-content: flex-end;
    width: auto;
    max-width: 75%;
    z-index: 102;
  }

  .primary-transaction {
    z-index: 103;
  }

  .secondary-transaction {
    margin-right: 4px;
  }

  .underlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: -1;
  }
</style>
